import { useEffect, useState } from 'react';

import { createTheme, FontLoader, ThemeProvider } from '@gympass/yoga';
import { ThemeProvider as MuiThemeProvider } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useFlagsStatus } from '@unleash/proxy-client-react';
import { wellzTheme as muiWellzTheme } from '@wellz-theme/mui';
import { intlAtom, MyIntlProvider } from 'atoms/intlAtom';
import { modalsConfig } from 'configurations';
import { ReadFlagsSync } from 'hocs/FlagsReadySync';
import { useAtomValue } from 'jotai';
import { BrowserRouter as Router } from 'react-router-dom';
import Routes from 'routes';

import {
  ModalsProvider,
  PermissionConfig,
  PermissionConfigProvider,
  PermissionDefaultState,
  Permissions,
  useModalsController,
} from 'context';
import { AgendaConfigs, AgendaProvider } from 'context/AgendaContext';
import {
  NotificationEvent,
  NotificationProvider,
} from 'context/NotificationContext';
import { ProgramsProvider } from 'context/ProgramsContext';
import {
  LoggedSpecialist,
  SpecialistProvider,
} from 'context/SpecialistContext';
import { SpecialistAvailability, SpecialistProfessionalInfo } from 'services';
import { getPermissions } from 'services/specialist/specialist';

import { PageContainer } from 'components';
import { LoadingPage } from 'pages';
import { ProgramProps } from 'pages/ContentLibrary/types';

import { CareMatrixProvider } from './context/CareMatrixContext';
import { ComplaintDeviceProvider } from './context/ComplaintDeviceContext';
import { GlobalStyle, wellzTheme } from './theme';

const URL_BASENAME = '/new-provider';

const App = () => {
  const { flagsReady } = useFlagsStatus();

  const [notification, setNotification] = useState<NotificationEvent | null>(
    null,
  );
  const [permissionsConfig, setPermissionsConfig] = useState<PermissionConfig>(
    PermissionDefaultState,
  );
  const [specialistInfo, setSpecialistInfo] = useState<LoggedSpecialist | null>(
    null,
  );
  const [specialistProfessionalInfo, setSpecialistProfessionalInfo] =
    useState<SpecialistProfessionalInfo | null>(null);
  const [isAuthenticated, setAuthenticated] = useState<boolean>();
  const [agenda, setAgenda] = useState<SpecialistAvailability | null>(null);
  const [agendaConfigs, setAgendaConfigs] = useState<AgendaConfigs | null>(
    null,
  );
  const [programs, setPrograms] = useState<ProgramProps[] | null>(null);

  const { Modals, modalController } = useModalsController(modalsConfig);

  /*
  https://github.com/remix-run/react-router/issues/8427
  react-router v6 does not redirects automatically to /basename, therefore we need this workaround
  */
  if (!window.location.pathname.includes(URL_BASENAME)) {
    window.history.replaceState(
      '',
      '',
      URL_BASENAME + window.location.pathname,
    );
  }

  const { data: permissions } = useQuery({
    queryKey: ['get-permissions'],
    queryFn: getPermissions,
  });

  const specialistHasGroups = !!specialistInfo?.groups?.length;
  const intl = useAtomValue(intlAtom);

  const showLoading = !(flagsReady && specialistHasGroups) || intl.loading;

  const formatPermissions = (allPermissions: Permissions) =>
    Object.entries(allPermissions).reduce((prev, [key, value]) => {
      const acc = { ...prev };

      if (key.includes('.')) {
        const newKey = key.slice(0, key.indexOf('.'));
        const subKey = key.slice(key.indexOf('.') + 1);

        acc[newKey] = {
          ...(acc[newKey] as Permissions),
          [subKey]: value,
        };
        return acc;
      }

      acc[key] = value;
      return acc;
    }, {} as PermissionConfig);

  const theme = createTheme(() => ({
    ...wellzTheme,
  }));

  useEffect(() => {
    try {
      if (!permissions) {
        setPermissionsConfig(PermissionDefaultState);
        return;
      }

      const formattedPermissions = formatPermissions(permissions);
      setPermissionsConfig(formattedPermissions);
    } catch (e) {
      setPermissionsConfig(PermissionDefaultState);
    }
  }, [permissions]);

  return (
    <MuiThemeProvider theme={muiWellzTheme}>
      <ThemeProvider theme={theme}>
        <FontLoader />
        <GlobalStyle color={wellzTheme.colors.textPrimary} />
        {showLoading && <LoadingPage />}
        <ReadFlagsSync>
          <MyIntlProvider>
            <NotificationProvider value={{ notification, setNotification }}>
              <ModalsProvider value={{ Modals, modalController }}>
                <PermissionConfigProvider
                  value={{ permissionsConfig, setPermissionsConfig }}
                >
                  <SpecialistProvider
                    value={{
                      specialistInfo,
                      setSpecialistInfo,
                      isAuthenticated,
                      setAuthenticated,
                      specialistProfessionalInfo,
                      setSpecialistProfessionalInfo,
                    }}
                  >
                    <ProgramsProvider value={{ programs, setPrograms }}>
                      <AgendaProvider
                        value={{
                          agenda,
                          setAgenda,
                          agendaConfigs,
                          setAgendaConfigs,
                        }}
                      >
                        <Router basename={URL_BASENAME}>
                          <CareMatrixProvider>
                            <ComplaintDeviceProvider>
                              <PageContainer
                                isAuthenticatedSpecialist={
                                  !!isAuthenticated && !!specialistInfo?.groups
                                }
                              >
                                <Routes />
                              </PageContainer>
                            </ComplaintDeviceProvider>
                            {Object.entries(Modals).map(
                              ([key, { Component, isOpen, props }]) => {
                                if (isOpen) {
                                  return (
                                    <Component
                                      isOpen
                                      {...props}
                                      key={key}
                                      modalKey={key}
                                    />
                                  );
                                }
                                return null;
                              },
                            )}
                          </CareMatrixProvider>
                        </Router>
                      </AgendaProvider>
                    </ProgramsProvider>
                  </SpecialistProvider>
                </PermissionConfigProvider>
              </ModalsProvider>
            </NotificationProvider>
          </MyIntlProvider>
        </ReadFlagsSync>
      </ThemeProvider>
    </MuiThemeProvider>
  );
};

export default App;
